import { XCircle } from "react-feather";
import clockIcon from "../../assets/images/clock-icon.svg";
import locationIcon from "../../assets/images/pin-icon.svg";
import { Button, Modal } from "../../components";
import useLanguage from "../../hooks/language";
import Clinic from "../../models/clinic";
import { getLocalizedAppointmentDateDetails } from "../../utils/appointment-display";
import { getClinicAddress } from "../../utils/clinic-helpers";
import styles from "./appointment-summary.module.css";

export default function AppointmentSummaryModal({
    appointmentDate,
    appointmentClinic,
    visible,
    onSubmit,
    onClose,
}: {
    appointmentDate: string;
    appointmentClinic: Clinic;
    visible: boolean;
    onSubmit: () => void;
    onClose: () => void;
}): React.ReactElement {
    const { translations, language } = useLanguage();
    const t = translations.appointmentSummaryModal;
    const appointmentDateDetails = getLocalizedAppointmentDateDetails(
        appointmentDate,
        language
    );
    const formattedAppointmentDate = appointmentDateDetails
        ? `${appointmentDateDetails.date}, ${appointmentDateDetails.time}`
        : appointmentDate;

    const closeModal = () => {
        if (!visible) return;
        onClose();
    };

    const clinicAddress = getClinicAddress(appointmentClinic);

    return (
        <Modal
            showHeader={false}
            visible={visible}
            onVisibilityChange={(visible) => {
                if (!visible) closeModal();
            }}
            className={styles["appointment-summary-modal"]}
        >
            <div className={styles["appointment-summary-modal__close"]}>
                <button
                    className={
                        styles["appointment-summary-modal__close-button"]
                    }
                    aria-label={translations.common.close}
                    onClick={closeModal}
                >
                    <XCircle aria-hidden="true" focusable="false" />
                </button>
            </div>

            <div className={styles["appointment-summary-modal__content"]}>
                <div
                    className={
                        styles["appointment-summary-modal__title-container"]
                    }
                >
                    <div className={styles["appointment-summary-modal__emoji"]}>
                        ⏱️
                    </div>

                    <h3 className={styles["appointment-summary-modal__title"]}>
                        {t.title}
                    </h3>

                    <div
                        className={
                            styles["appointment-summary-modal__description"]
                        }
                    >
                        {t.description}
                    </div>
                </div>

                <div className={styles["appointment-summary-modal__datetime"]}>
                    <img
                        src={clockIcon}
                        className={
                            styles["appointment-summary-modal__datetime-icon"]
                        }
                    />
                    {formattedAppointmentDate}
                </div>

                <div className={styles["appointment-summary-modal__location"]}>
                    <img
                        src={locationIcon}
                        className={
                            styles["appointment-summary-modal__location-icon"]
                        }
                    />
                    {appointmentClinic && (
                        <div
                            className={
                                styles[
                                    "appointment-summary-modal__location__details"
                                ]
                            }
                        >
                            <div
                                className={
                                    styles[
                                        "appointment-summary-modal__location__details__name"
                                    ]
                                }
                            >
                                {appointmentClinic?.name}
                            </div>
                            <div>{clinicAddress}</div>
                        </div>
                    )}
                </div>

                <Button
                    type="primary"
                    className={styles["appointment-summary-modal__cta"]}
                    onClick={onSubmit}
                >
                    {t.cta}
                </Button>
            </div>
        </Modal>
    );
}
